import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bold, Italic, Underline, Link as LinkIcon, Unlink, List, Heading as HeadingIcon } from "lucide-react";

/**
 * Minimal rich-text editor storing HTML.
 * - Selection-driven floating toolbar (bold / italic / underline / link / list)
 * - Uses document.execCommand for maximum simplicity; fine for MVP.
 * - Backwards compatible: if value is plain text (no tags), renders as-is.
 */
export default function RichText({ value, onChange, placeholder, className = "", multiline = true, testId }) {
  const ref = useRef(null);
  const [tb, setTb] = useState({ show: false, x: 0, y: 0, active: {} });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  // Set initial content once (avoid caret-reset on every keystroke).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep in sync if parent replaces value (e.g. after block reorder).
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const updateToolbar = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !ref.current?.contains(sel.anchorNode)) {
      setTb((t) => ({ ...t, show: false }));
      setLinkOpen(false);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    setTb({
      show: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      active: {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      },
    });
  };

  const cmd = (name, val) => {
    ref.current?.focus();
    document.execCommand(name, false, val);
    emit();
    updateToolbar();
  };

  const applyLink = () => {
    if (!linkUrl) return;
    const url = /^https?:\/\//.test(linkUrl) ? linkUrl : `https://${linkUrl}`;
    cmd("createLink", url);
    setLinkOpen(false);
    setLinkUrl("");
  };

  const onKey = (e) => {
    if (!multiline && e.key === "Enter") { e.preventDefault(); ref.current?.blur(); }
    if (e.metaKey || e.ctrlKey) {
      if (e.key === "b") { e.preventDefault(); cmd("bold"); }
      else if (e.key === "i") { e.preventDefault(); cmd("italic"); }
      else if (e.key === "u") { e.preventDefault(); cmd("underline"); }
    }
    updateToolbar();
  };

  return (
    <>
      <div
        ref={ref}
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onMouseUp={updateToolbar}
        onKeyUp={updateToolbar}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => { setTb((t) => ({ ...t, show: false })); setLinkOpen(false); }, 150)}
        className={`bh-rt outline-none ${className}`}
        data-placeholder={placeholder || ""}
      />
      {tb.show && createPortal(
        <div
          className="fixed z-[100] flex items-center gap-0.5 bg-[#3a372b] text-[#e8e4e1] rounded-full px-1.5 py-1 bh-hard-shadow-sm border border-[#3a372b]"
          style={{ left: tb.x, top: tb.y, transform: "translate(-50%, -100%)" }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <TbBtn active={tb.active.bold} onClick={() => cmd("bold")} title="Vet (Cmd/Ctrl+B)"><Bold className="w-3.5 h-3.5" /></TbBtn>
          <TbBtn active={tb.active.italic} onClick={() => cmd("italic")} title="Cursief (Cmd/Ctrl+I)"><Italic className="w-3.5 h-3.5" /></TbBtn>
          <TbBtn active={tb.active.underline} onClick={() => cmd("underline")} title="Onderstreept (Cmd/Ctrl+U)"><Underline className="w-3.5 h-3.5" /></TbBtn>
          <div className="w-px h-4 bg-[#e8e4e1]/30 mx-0.5" />
          {multiline && (
            <TbBtn onClick={() => cmd("insertUnorderedList")} title="Lijst"><List className="w-3.5 h-3.5" /></TbBtn>
          )}
          {linkOpen ? (
            <div className="flex items-center gap-1 ml-1">
              <input
                autoFocus
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } }}
                placeholder="https://…"
                className="h-6 w-40 rounded-full bg-[#e8e4e1] text-[#3a372b] text-xs px-2 outline-none"
                data-testid="rt-link-input"
              />
              <TbBtn onClick={applyLink} title="Link toepassen"><LinkIcon className="w-3.5 h-3.5" /></TbBtn>
            </div>
          ) : (
            <>
              <TbBtn onClick={() => setLinkOpen(true)} title="Link toevoegen"><LinkIcon className="w-3.5 h-3.5" /></TbBtn>
              <TbBtn onClick={() => cmd("unlink")} title="Link verwijderen"><Unlink className="w-3.5 h-3.5" /></TbBtn>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function TbBtn({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`h-7 w-7 flex items-center justify-center rounded-full transition-colors ${active ? "bg-[#fef067] text-[#3a372b]" : "hover:bg-white/10"}`}
    >
      {children}
    </button>
  );
}
